import { Action, Dispatch } from 'redux';
import { IAppState } from '../reducers';
import {
  INamingTemplatePart,
  getNamingTemplates,
  getNamingTemplateParts,
  updateNamingTemplates,
  INamingTemplatePutData,
  INamingTemplateData,
} from '../../api/vault/namingTemplates';
import { ActionType as ValidationAction } from '../validation';
import { Validation } from '../../constants/validation';
import { getLocale } from '../localization';
import { LocalizationLabels } from '../../enums';

/* eslint-disable no-param-reassign */

export enum ValidationKey {
  Email = 'NotificationSettingsEmail',
  Phone = 'NotificationSettingsPhone',
}

export const ValidationRules = {
  Email: [Validation.NOT_EMPTY, Validation.EMAIL],
};

const qualifierString = 'Qualifier';

export interface IState {
  isOpen: boolean;
  isLoading: boolean;
  _id: string;
  namingTemplate: Array<INamingTemplatePart>;
  preview: string;
  fullNamingTemplate: INamingTemplateData;
}

export enum ActionType {
  Toggle = 'ExportTemplateSettings.Toggle',
  StartLoading = 'ExportTemplateSettings.StartLoading',
  FinishLoading = 'ExportTemplateSettings.FinishLoading',
  UpdateSettings = 'ExportTemplateSettings.UpdateSettings',
  Clear = 'ExportTemplateSettings.Clear',
  UpdateNamingTemplate = 'ExportTemplateSettings.UpdateNamingTemplate',
}
export interface IExchangeNotificationSettingsAction extends Action<ActionType> {
  payload?: any,
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isLoading: false,
  _id: '',
  namingTemplate: [{
    _id: '',
    displayName: '',
    order: 0,
  }],
  preview: '',
  fullNamingTemplate: null,
};

export default function reducer(state: IState = INITIAL_STATE, action: IExchangeNotificationSettingsAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.FinishLoading:
      return {
        ...state,
        isLoading: false,
      };
    case ActionType.UpdateSettings:
      return {
        ...state,
        namingTemplate: action.payload.namingTemplate,
        _id: action.payload._id,
      };
    case ActionType.UpdateNamingTemplate:
      return {
        ...state,
        fullNamingTemplate: action.payload.fullNamingTemplate,
      };
    case ActionType.Clear:
      return INITIAL_STATE;
    default:
      return state;
  }
}

export const toggleExportTemplateSettings = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ValidationAction.ClearErrors,
    key: ValidationKey.Email,
  });
  dispatch({
    type: ActionType.Toggle,
  });
};

export const clearExportTemplateSettings = () => ({
  type: ActionType.Clear,
});

export const queryExportTemplateSettings = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.StartLoading,
  });

  const state = getState();
  const currentSport = state.exchange.initExchange.sport;

  const responseOrgDefault = await getNamingTemplates();
  const namingTemplatesInUse = responseOrgDefault.data.data;

  dispatch({
    type: ActionType.UpdateNamingTemplate,
    payload: {
      fullNamingTemplate: namingTemplatesInUse,
    },
  });

  if (!namingTemplatesInUse) return;

  // Hide parts that are for other sports
  const namingTemplate: INamingTemplatePart[] = namingTemplatesInUse.namingTemplateParts.filter((part) => part.sport.length === 0 || part.sport.includes(currentSport));

  const responseTemplateParts = await getNamingTemplateParts();
  const namingTemplateAllParts: INamingTemplatePart[] = responseTemplateParts.data.data;
  // Get all parts into namingTemplate
  namingTemplateAllParts.forEach((value) => {
    const item = value;
    item.order = -1; // Not needed when not in use

    const existingItems = namingTemplate.filter((e) => e.tagName === value.tagName);

    if (existingItems != null && existingItems.length > 0) {
      existingItems.forEach((existingItem, index) => {
        if (existingItem.displayName === '' || existingItem.refresh) {
          existingItems[index].displayName = value.displayName;
        }
      });
    } else {
      namingTemplate.push(item);
    }
  });

  // TODO use sport filter with new template parts that have the correct labels and sample values according sport
  const phaseLabel = getLocale(getState(), LocalizationLabels.ExchangePhase);
  const phaseSampleValue = getLocale(getState(), LocalizationLabels.ExchangePhaseSampleValue);
  namingTemplate.forEach((templatePart) => {
    if (templatePart.displayName === qualifierString) {
      if (phaseLabel.length !== 0) templatePart.displayName = phaseLabel;
      if (phaseSampleValue.length !== 0) templatePart.sampleValue = phaseSampleValue;
    }
  });

  const { _id } = namingTemplatesInUse;

  dispatch({
    type: ActionType.UpdateSettings,
    payload: { _id, namingTemplate },
  });

  dispatch({
    type: ActionType.FinishLoading,
  });
};

export const updateSettings = (namingTemplate: INamingTemplatePart) => ({
  type: ActionType.UpdateSettings,
  payload: {
    namingTemplate,
  },
});

export const saveSettings = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const currentSport = state.exchange.initExchange.sport;

  const {
    exchange: {
      exporttemplatesettings: {
        _id,
        namingTemplate: finalTemplateParts,
        fullNamingTemplate: {
          originalFileName, namingTemplateParts: originalTemplateParts,
        },
      },
    },
  } = state;

  // Add hidden parts back in so they are not lost after saving
  // This allows flexibity to easily hide and show parts according to sport
  originalTemplateParts.filter((part) => part.sport.length !== 0 && !part.sport.includes(currentSport)).forEach((part) => {
    part.isUsed = false;
    finalTemplateParts.push(part);
  });

  // Update final order here
  let index = 1;
  finalTemplateParts.map((e) => {
    if (e.isUsed) {
      e.order = index;
      index += 1;
    } else {
      e.order = -1;
    }
    return e;
  });

  const dataPut: INamingTemplatePutData = {
    name: 'Template Name',
    namingTemplateParts: finalTemplateParts,
    originalFileName,
  };

  const response = await updateNamingTemplates(_id, dataPut);

  if (response.status !== 200) {
    // TODO: Note response status for success should be 204
    console.error('[QA]: Could not save namingTemplate');
  }
};

export const updateOriginalFilename = (originalFileName: boolean) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = await getState();
  dispatch({
    type: ActionType.UpdateNamingTemplate,
    payload: {
      fullNamingTemplate: {
        ...state.exchange.exporttemplatesettings.fullNamingTemplate,
        originalFileName,
      },
    },
  });
};
